/* eslint-disable max-len */
/* istanbul ignore file */
/* eslint-disable react/no-unused-prop-types */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import _, { mapObject, constant } from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { withPerfMarker } from '@bingads-webui/performance-marker-react-wrapper';
import {
  Panel,
  Pivot,
  PivotItem,
  PrimaryButton,
  DefaultButton,
  PanelType,
  Checkbox,
  Dialog,
  DialogType,
  DialogFooter,
} from '@bingads-webui-react/fabric-control';
import { getEditVideoStyle } from '../../style/js/edit-video';
import { useThumbnailEditor } from './thumbnail';
import { useCaptionEditor } from './caption';
import { useCropperEditor } from './cropper';
import { useClipperEditor } from './clipper';
import { ImageExperienceVideo } from '../model/image-experience-video';
import { CustomThumbnailRecordType } from '../types';
import { FormatVideoErrorForVideoEditor } from '../util/format-video-error';
import C from '../constants';
import { VideoEditor } from './constants';

const PivotLinkFormat = {
  tabs: 1,
};

const getPivotContentStyle = hidden => (hidden ? { position: 'absolute', left: 2000, top: 0 } : {});

const EditVideoComponent = (props) => {
  const {
    classes,
    onDismiss,
    i18n,
    video,
    onSaveThumbnail,
    onCropped,
    onClipped,
    videoRepository,
    permissions,
  } = props;
  const {
    IsVideoAdEditorCropClipEnabled,
    IsVideoAdEditorThumbnailEnabled,
    IsVideoAdEditorCaptionEnabled,
  } = permissions;

  // eslint-disable-next-line no-nested-ternary
  const [selectedEditor, setSelectedEditor] = React.useState(video.Status === 'Active' && IsVideoAdEditorCropClipEnabled ?
    VideoEditor.Clipper :
    (IsVideoAdEditorThumbnailEnabled ? VideoEditor.Thumbnail : null));
  const [errors, setErrors] = React.useState([]);
  const [shouldShowApplyClipNotice, setShouldShowApplyClipNotice] = React.useState(false);
  const [isApplyTrimToAllAspectRatio, setIsApplyTrimToAllAspectRatio] = React.useState(true);
  const [blockUi, setBlockUi] = React.useState(false);

  const clipperErrors = useMemo(
    () => errors.filter(({ scenario }) => !scenario || scenario === VideoEditor.Clipper),
    [errors]
  );
  const cropperErrors = useMemo(
    () => errors.filter(({ scenario }) => !scenario || scenario === VideoEditor.Cropper),
    [errors]
  );

  const {
    component: Thumbnail,
    getData: getThumbnails,
  } = useThumbnailEditor(props);
  const {
    component: Captions,
    captions,
  } = useCaptionEditor(props);
  const {
    component: Cropper,
    getData: getCropData,
  } = useCropperEditor({ ...props, errors: cropperErrors });
  const {
    component: Clipper,
    isSelectionOutofSync,
    getData: getClipData,
  } = useClipperEditor({ ...props, errors: clipperErrors });

  const defaultError = useMemo(() => ({
    message: i18n.getString('ImageExperience_Video_UploadError_NoInternet'),
  }), [i18n]);

  const shouldDisableApply = useMemo(() => (
    (selectedEditor === VideoEditor.Clipper) && isSelectionOutofSync
  ), [isSelectionOutofSync, selectedEditor]);

  const onErrors = (error) => {
    const errorList = FormatVideoErrorForVideoEditor(selectedEditor, error, i18n) || defaultError;
    setErrors(errorList);
  };

  const applyEditSetting = (setting, callback, options) => {
    if (_.isEmpty(setting)) {
      onDismiss();
      return;
    }
    setBlockUi(true);
    videoRepository.createVideoBasedOnOriginalVideo({
      id: video.IdString,
      data: {
        CropWidth: setting.width,
        CropHeight: setting.height,
        CropX: setting.x,
        CropY: setting.y,
        StartClipTime: setting.start,
        EndClipTime: setting.end,
        Description: setting.name,
      },
    })
      .then((response) => {
        if (_.isFunction(callback)) {
          callback(response, video.Type, { originalIdString: video.IdString, ...options });
        }
      })
      .catch(onErrors)
      .finally(() => setBlockUi(false));
  };

  const onApplyEditClick = () => {
    if (selectedEditor === VideoEditor.Thumbnail) {
      const thumbnailRecord = getThumbnails();
      if (thumbnailRecord) {
        onSaveThumbnail({
          thumbnailRecord,
        });
      } else {
        onDismiss();
      }
    }
    if (selectedEditor === VideoEditor.Clipper) {
      const settings = getClipData();
      if (!_.isEmpty(settings) && captions.length > 0) {
        setShouldShowApplyClipNotice(true);
      } else {
        applyEditSetting(settings, onClipped, { isApplyTrimToAllAspectRatio });
      }
    }

    if (selectedEditor === VideoEditor.Cropper) {
      const setting = getCropData();
      applyEditSetting(setting, onCropped);
    }
  };

  const onProceedApplyClip = () => {
    setShouldShowApplyClipNotice(false);
    const settings = getClipData();
    applyEditSetting(settings, onClipped, { isApplyTrimToAllAspectRatio });
  };

  const onRenderFooterContent = () => (
    <div className="footer-container">
      <PrimaryButton
        onClick={onApplyEditClick}
        text={i18n.getString('Apply')}
        disabled={blockUi || shouldDisableApply}
      />
      <DefaultButton onClick={() => onDismiss()} text={i18n.getString('Audience_Campaign_Cancel_Label')} />
      {
        selectedEditor === VideoEditor.Clipper
        ? <Checkbox
            className="apply-to-all"
            label={i18n.getString('ImageExperience_ApplyTrimAndCaptionToAllRatio', {
              effects:
                IsVideoAdEditorCropClipEnabled && IsVideoAdEditorCaptionEnabled ?
                  i18n.getString('ImageExperience_EditVideo_Trim_And_Captions') :
                  i18n.getString('ImageExperience_EditVideo_Trim_Only'),
            })}
            onChange={() => setIsApplyTrimToAllAspectRatio(!isApplyTrimToAllAspectRatio)}
            checked={isApplyTrimToAllAspectRatio}
        />
        : null
      }
    </div>);

  const onCloseApplyClipNotice = () => setShouldShowApplyClipNotice(false);

  return (
    <Panel
      isOpen
      onDismiss={onDismiss}
      type={PanelType.custom}
      hasCloseButton
      customWidth={1000}
      className={classes.root}
      headerText={`${i18n.getString('ImageExperience_EditVideo')} ${i18n.getString(C.videoAspectRatios[video.Type].stringKey)}`}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom
      layerProps={{
        eventBubblingEnabled: true,
        className: 'edit-video-layer',
      }}
    >
      <UiBlocker
        blocking={blockUi}
        loadingText={i18n.getString('ImageExperience_Video_Status_Encoding')}
      >
        <div className="edit-video-container">
          <div className="edit-video-description">
            {/* PLACEHOLDER */i18n.getString('ImageExperience_Video_Editor_Description')}
            <LearnMoreLink
              linkText={i18n.getString('Learn more')}
              topic="" /* PLACEHOLDER */
            />
          </div>
          <Pivot
            linkFormat={PivotLinkFormat.tabs}
            onLinkClick={item => setSelectedEditor(item.props.itemKey)}
            selectedKey={selectedEditor}
            headersOnly
          >
            { IsVideoAdEditorCropClipEnabled &&
              <PivotItem itemKey="Clipper" headerText={i18n.getString('ImageExperience_EditVideo_Trim')} headerButtonProps={{ disabled: video.Status !== 'Active' }} />
            }
            { IsVideoAdEditorCropClipEnabled &&
              <PivotItem itemKey="Cropper" headerText={i18n.getString('ImageExperience_EditVideo_Crop')} headerButtonProps={{ disabled: video.Status !== 'Active' }} />
            }
            { IsVideoAdEditorThumbnailEnabled &&
              <PivotItem itemKey="Thumbnail" headerText={i18n.getString('ImageExperience_EditVideo_Thumbnail')} />
            }
          </Pivot>
          <div style={getPivotContentStyle(!(IsVideoAdEditorCropClipEnabled && selectedEditor === VideoEditor.Clipper))} >
            {Clipper}
            {IsVideoAdEditorCaptionEnabled && Captions}
          </div>
          <div style={getPivotContentStyle(!(IsVideoAdEditorCropClipEnabled && selectedEditor === VideoEditor.Cropper))}>
            {Cropper}
          </div>
          <div style={getPivotContentStyle(!(IsVideoAdEditorThumbnailEnabled && selectedEditor === VideoEditor.Thumbnail))}>
            {Thumbnail}
          </div>
        </div>
        <Dialog
          hidden={!shouldShowApplyClipNotice}
          onDismiss={onCloseApplyClipNotice}
          dialogContentProps={{
            type: DialogType.normal,
            title: i18n.getString('ImageExperience_ApplyClipNoticeTitle'),
          }}
          modalProps={{ isBlocking: true, className: 'apply-clip-notice-dialog' }}
        >
          <div>{i18n.getString('ImageExperience_ApplyClipNoticeDescription')}</div>
          <DialogFooter>
            <PrimaryButton onClick={onProceedApplyClip} text={i18n.getString('ImageExperience_Proceed')} />
            <DefaultButton onClick={onCloseApplyClipNotice} text={i18n.getString('ImageExperience_Cancel')} />
          </DialogFooter>
        </Dialog>
      </UiBlocker>
    </Panel>
  );
};

EditVideoComponent.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape(mapObject(
    getEditVideoStyle({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  oDataConfig: PropTypes.shape({
    odataRootUrl: PropTypes.string,
    odataPath: PropTypes.string,
    odataToken: PropTypes.string,
    applicationName: PropTypes.string,
    oDataTokenType: PropTypes.string,
  }),
  isLazyLoadEnabled: PropTypes.bool,
  isStockPhotoEnabled: PropTypes.bool,
  activityLogger: PropTypes.shape({
    signal: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
  }),
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  staticImagePath: PropTypes.string.isRequired,
  onDismiss: PropTypes.func,
  onSaveThumbnail: PropTypes.func,
  onCropped: PropTypes.func,
  onClipped: PropTypes.func,
  video: PropTypes.instanceOf(ImageExperienceVideo).isRequired,
  thumbnailRecord: CustomThumbnailRecordType,
  videoRepository: PropTypes.shape({
    createVideoBasedOnOriginalVideo: PropTypes.func.isRequired,
  }).isRequired,
  resourceRepository: PropTypes.shape({
    createResource: PropTypes.func.isRequired,
    getResources: PropTypes.func.isRequired,
    deleteResource: PropTypes.func.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({
    IsVideoAdEditorCropClipEnabled: PropTypes.bool,
    IsVideoAdEditorThumbnailEnabled: PropTypes.bool,
    IsVideoAdEditorCaptionEnabled: PropTypes.bool,
  }),
};

EditVideoComponent.defaultProps = {
  onDismiss: _.noop,
  onSaveThumbnail: _.noop,
  onCropped: _.noop,
  onClipped: _.noop,
  oDataConfig: {},
  isLazyLoadEnabled: true,
  isStockPhotoEnabled: true,
  activityLogger: {
    signal: _.noop,
    error: _.noop,
  },
  thumbnailRecord: {},
  permissions: {},
};

const EditVideo = withDefaultStyles(withPerfMarker(EditVideoComponent), getEditVideoStyle);

export { EditVideo, EditVideoComponent };
